import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  Sparkles,
  Loader2,
  CheckCircle2,
  Award,
  GraduationCap,
  Wand2,
} from 'lucide-react';
import { CvAnalysisResult } from '../types/userCv';
import { analyzeFromConnect, applyCvUpdate } from '../utils/userCvApi';
import { useApiClient } from '../hooks/useApiClient';

interface Props {
  employeeId: string;
  connectId: number;
  onClose: () => void;
  onSuccess: (newVersion: number) => void;
}

/**
 * Modal flow: analyze -> show preview with checkboxes -> apply only selected items.
 */
export function CvUpdatePreviewModal({ employeeId, connectId, onClose, onSuccess }: Props) {
  const { apiCall } = useApiClient();
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<CvAnalysisResult | null>(null);

  // Selection state — default: all checked.
  const [skillSel, setSkillSel] = useState<Set<number>>(new Set());
  const [certSel, setCertSel] = useState<Set<number>>(new Set());
  const [learnSel, setLearnSel] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await analyzeFromConnect(employeeId, connectId, apiCall);
        if (cancelled) return;
        setAnalysis(result);
        setSkillSel(new Set(result.newSkills?.map((_, i) => i) ?? []));
        setCertSel(new Set(result.newCertifications?.map((_, i) => i) ?? []));
        setLearnSel(new Set(result.newLearnings?.map((_, i) => i) ?? []));
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'AI analysis failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, connectId]);

  const toggle = (set: Set<number>, idx: number, setter: (s: Set<number>) => void) => {
    const next = new Set(set);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    setter(next);
  };

  // Build the filtered analysis payload that will actually be applied.
  const filteredAnalysis = useMemo<CvAnalysisResult | null>(() => {
    if (!analysis) return null;
    const skills = (analysis.newSkills ?? []).filter((_, i) => skillSel.has(i));
    const certs = (analysis.newCertifications ?? []).filter((_, i) => certSel.has(i));
    const learns = (analysis.newLearnings ?? []).filter((_, i) => learnSel.has(i));

    // Rebuild updatedCv by stripping excluded items if present.
    let updatedCv = analysis.updatedCv;
    if (updatedCv && analysis.updatedCv) {
      const droppedSkills = new Set(
        (analysis.newSkills ?? []).filter((_, i) => !skillSel.has(i)).map(s => s.toLowerCase())
      );
      const droppedCertNames = new Set(
        (analysis.newCertifications ?? []).filter((_, i) => !certSel.has(i)).map(c => c.name.toLowerCase())
      );
      const droppedLearnTitles = new Set(
        (analysis.newLearnings ?? []).filter((_, i) => !learnSel.has(i)).map(l => l.title.toLowerCase())
      );
      updatedCv = {
        ...updatedCv,
        skills: (updatedCv.skills ?? []).filter(s => !droppedSkills.has(s.toLowerCase())),
        certifications: (updatedCv.certifications ?? []).filter(c => !droppedCertNames.has(c.name.toLowerCase())),
        learnings: (updatedCv.learnings ?? []).filter(l => !droppedLearnTitles.has(l.title.toLowerCase())),
      };
    }

    return {
      ...analysis,
      newSkills: skills,
      newCertifications: certs,
      newLearnings: learns,
      updatedCv,
    };
  }, [analysis, skillSel, certSel, learnSel]);

  const selectedCount = skillSel.size + certSel.size + learnSel.size;

  const handleApply = async () => {
    if (!filteredAnalysis?.updatedCv || selectedCount === 0) return;
    setApplying(true);
    setError(null);
    try {
      const newVersion = await applyCvUpdate(employeeId, connectId, filteredAnalysis, apiCall);
      onSuccess(newVersion.version);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to apply update');
    } finally {
      setApplying(false);
    }
  };

  const nothingToAdd = analysis &&
    (analysis.newSkills?.length ?? 0) === 0 &&
    (analysis.newCertifications?.length ?? 0) === 0 &&
    (analysis.newLearnings?.length ?? 0) === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 px-6 py-5 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Wand2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Update CV from this Connect</h2>
              <p className="text-xs text-white/85 mt-0.5">Pick the items you want to add. AI suggestions are checked by default.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 gap-3">
              <Loader2 className="w-7 h-7 animate-spin text-pink-500" />
              <p className="text-sm font-medium">Analyzing your Connect with AI…</p>
              <p className="text-xs text-slate-400">This usually takes 5-15 seconds.</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
          ) : analysis ? (
            <div className="space-y-5">
              {analysis.summary && (
                <div className="bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-100 rounded-xl p-4">
                  <p className="text-xs font-bold text-pink-600 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> AI summary
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">{analysis.summary}</p>
                </div>
              )}

              {nothingToAdd ? (
                <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm font-medium text-slate-500">No new items detected</p>
                  <p className="text-xs mt-1">The AI didn't find new skills/certifications/learnings to add from this Connect.</p>
                </div>
              ) : (
                <>
                  <SelectableSection
                    title="New skills"
                    icon={<Sparkles className="w-4 h-4 text-pink-600" />}
                    items={analysis.newSkills ?? []}
                    selected={skillSel}
                    onToggle={(i) => toggle(skillSel, i, setSkillSel)}
                    onSelectAll={() => setSkillSel(new Set((analysis.newSkills ?? []).map((_, i) => i)))}
                    onClearAll={() => setSkillSel(new Set())}
                    renderItem={(s) => <span className="text-sm font-medium text-slate-800">+ {s}</span>}
                    accent="pink"
                  />

                  <SelectableSection
                    title="New certifications"
                    icon={<Award className="w-4 h-4 text-purple-600" />}
                    items={analysis.newCertifications ?? []}
                    selected={certSel}
                    onToggle={(i) => toggle(certSel, i, setCertSel)}
                    onSelectAll={() => setCertSel(new Set((analysis.newCertifications ?? []).map((_, i) => i)))}
                    onClearAll={() => setCertSel(new Set())}
                    renderItem={(c) => (
                      <span className="text-sm">
                        <span className="font-semibold text-slate-800">{c.name}</span>
                        {c.issuer && <span className="text-slate-500"> · {c.issuer}</span>}
                        {c.date && <span className="text-slate-400"> · {c.date}</span>}
                      </span>
                    )}
                    accent="purple"
                  />

                  <SelectableSection
                    title="New learnings"
                    icon={<GraduationCap className="w-4 h-4 text-indigo-600" />}
                    items={analysis.newLearnings ?? []}
                    selected={learnSel}
                    onToggle={(i) => toggle(learnSel, i, setLearnSel)}
                    onSelectAll={() => setLearnSel(new Set((analysis.newLearnings ?? []).map((_, i) => i)))}
                    onClearAll={() => setLearnSel(new Set())}
                    renderItem={(l) => (
                      <div className="text-sm">
                        <p className="font-semibold text-slate-800">
                          {l.title}
                          {l.date && <span className="text-slate-400 font-normal"> · {l.date}</span>}
                        </p>
                        {l.description && <p className="text-slate-600 mt-0.5">{l.description}</p>}
                      </div>
                    )}
                    accent="indigo"
                  />
                </>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3 bg-slate-50 flex-wrap">
          <p className="text-xs text-slate-500">
            {nothingToAdd ? 'Nothing to add' : `${selectedCount} item${selectedCount === 1 ? '' : 's'} selected`}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm text-slate-600 hover:text-slate-800 border border-slate-300 bg-white"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={loading || applying || !!error || !filteredAnalysis?.updatedCv || selectedCount === 0}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-sm shadow-pink-500/20 disabled:opacity-50"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {applying ? 'Saving…' : 'Apply & save as new version'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectableSection<T>({
  title, icon, items, selected, onToggle, onSelectAll, onClearAll, renderItem, accent,
}: {
  title: string;
  icon: React.ReactNode;
  items: T[];
  selected: Set<number>;
  onToggle: (i: number) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  renderItem: (item: T) => React.ReactNode;
  accent: 'pink' | 'purple' | 'indigo';
}) {
  if (items.length === 0) return null;
  const accentBg = accent === 'pink' ? 'bg-pink-50 border-pink-100'
    : accent === 'purple' ? 'bg-purple-50 border-purple-100'
    : 'bg-indigo-50 border-indigo-100';
  const accentText = accent === 'pink' ? 'text-pink-700'
    : accent === 'purple' ? 'text-purple-700'
    : 'text-indigo-700';

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">{title}</h3>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full bg-white border ${accentText}`}>
            {selected.size}/{items.length}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <button onClick={onSelectAll} className="text-slate-500 hover:text-slate-800 font-semibold">All</button>
          <span className="text-slate-300">·</span>
          <button onClick={onClearAll} className="text-slate-500 hover:text-slate-800 font-semibold">None</button>
        </div>
      </div>
      <ul className="space-y-2">
        {items.map((item, i) => {
          const isOn = selected.has(i);
          return (
            <li
              key={i}
              onClick={() => onToggle(i)}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                isOn ? accentBg : 'bg-white border-slate-200 opacity-60'
              }`}
            >
              <input
                type="checkbox"
                checked={isOn}
                onChange={() => onToggle(i)}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 accent-pink-600 w-4 h-4 cursor-pointer"
              />
              <div className="flex-1 min-w-0">{renderItem(item)}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
